<template>
    <div class="album-node" :class="`album-node--${node.type}`">
        <!-- Paper node -->
        <div v-if="node.type === 'paper'" class="node-row paper-row">
            <div class="row-name">
                <el-checkbox v-model="node.checked" class="row-check" @change="handleCheck" @click.stop />
                <span class="node-icon icon-paper"><el-icon>
                        <Document />
                    </el-icon></span>
                <span class="paper-name clickable" @click="emitView(node.paperId)">{{ node.label }}</span>
                <el-tag v-if="node.paperMeta" size="small" type="danger" effect="light" round class="paper-tag">
                    {{ node.paperMeta }}
                </el-tag>
            </div>

            <div class="row-meta">
                <span class="meta-cell date"><el-icon>
                        <Clock />
                    </el-icon>{{ formatDate(node.createdAt || node.CreatedAt, true) }}</span>
                <span class="meta-cell price">3 储值</span>
                <span class="meta-cell downloads"><el-icon>
                        <Download />
                    </el-icon>{{ node.downloads || 0 }}</span>
                <div class="meta-cell actions">
                    <button class="icon-btn" title="预览试卷" @click.stop="emitView(node.paperId)">
                        <el-icon>
                            <View />
                        </el-icon>
                    </button>
                    <button class="icon-btn" title="加入试题篮" @click.stop>
                        <el-icon>
                            <ShoppingCart />
                        </el-icon>
                    </button>
                </div>
            </div>
        </div>

        <!-- Folder node -->
        <div v-else class="folder-node">
            <div class="folder-head" :class="{ 'is-open': expanded }" @click="toggleExpand">
                <el-checkbox v-model="node.checked" class="row-check" @change="handleFolderCheck" @click.stop />
                <span class="node-icon icon-folder"><el-icon>
                        <FolderOpened v-if="expanded" />
                        <Folder v-else />
                    </el-icon></span>
                <span class="folder-name">{{ node.label }}</span>
                <span class="folder-count" v-if="childCount">{{ childCount }} 份</span>
                <el-icon class="chevron" :class="{ 'is-open': expanded }" v-if="childCount">
                    <ArrowRight />
                </el-icon>
            </div>

            <el-collapse-transition>
                <div class="children" v-show="expanded && childCount > 0">
                    <AlbumStructureNode v-for="child in node.children" :key="child.id" :node="child"
                        :depth="depth + 1" :is-root="false" @node-change="emitChange" @view-paper="emitView" />
                </div>
            </el-collapse-transition>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import {
    Document, View, ShoppingCart, Folder, FolderOpened, ArrowRight, Clock, Download
} from '@element-plus/icons-vue'

const props = defineProps({
    node: {
        type: Object,
        required: true
    },
    depth: {
        type: Number,
        default: 0
    },
    isRoot: {
        type: Boolean,
        default: true
    }
})

const emits = defineEmits(['node-change', 'view-paper'])

const expanded = ref(true)
const childCount = computed(() => props.node.children?.length || 0)

const toggleExpand = () => {
    if (childCount.value) expanded.value = !expanded.value
}

const formatDate = (dateStr, showDateOnly = false) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    const formatted = showDateOnly ? d.toLocaleDateString() : d.toLocaleString()
    return formatted === 'Invalid Date' ? '-' : formatted
}

const handleCheck = () => {
    emits('node-change')
}

const handleFolderCheck = (val) => {
    // Check/Uncheck all children recursively
    toggleChildren(props.node, val)
    emits('node-change')
}

const toggleChildren = (node, checked) => {
    if (node.children) {
        node.children.forEach(child => {
            child.checked = checked
            toggleChildren(child, checked)
        })
    }
}

const emitChange = () => {
    emits('node-change')
}

const emitView = (id) => {
    emits('view-paper', id)
}
</script>

<style scoped>
.album-node {
    position: relative;
}

/* ---------- Paper row ---------- */
.node-row,
.folder-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 20px;
    min-height: 56px;
    background: #fff;
    transition: background 0.2s ease;
}

.paper-row {
    border-bottom: 1px solid #f0f3f9;
}

.paper-row:hover {
    background: #f6faff;
}

.row-name {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.row-check {
    flex-shrink: 0;
}

.node-icon {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 16px;
}

.icon-paper {
    background: linear-gradient(135deg, #fff5e6, #ffe8c2);
    color: #e6920a;
}

.icon-folder {
    background: linear-gradient(135deg, #eef4ff, #dcebff);
    color: #3d7eff;
}

.paper-name {
    font-size: 14px;
    color: #2f3b52;
    font-weight: 500;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.paper-name.clickable:hover {
    color: #3d7eff;
    cursor: pointer;
}

.paper-tag {
    flex-shrink: 0;
}

/* ---------- Meta columns ---------- */
.row-meta {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.meta-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 13px;
    color: #8b95a7;
    width: 110px;
    text-align: center;
}

.meta-cell .el-icon {
    font-size: 13px;
}

.meta-cell.price {
    width: 90px;
    color: #e6920a;
    font-weight: 600;
}

.meta-cell.downloads {
    width: 100px;
}

.meta-cell.actions {
    width: 96px;
    gap: 8px;
}

.icon-btn {
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 9px;
    background: #f2f5fa;
    color: #7a8699;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.icon-btn:hover {
    background: #e5efff;
    color: #3d7eff;
    transform: translateY(-1px);
}

/* ---------- Folder head ---------- */
.folder-node {
    position: relative;
}

.folder-head {
    cursor: pointer;
    background: #fbfcff;
    border-bottom: 1px solid #f0f3f9;
}

.folder-head:hover {
    background: #f1f6ff;
}

.folder-name {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: #37465f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.folder-count {
    flex-shrink: 0;
    font-size: 12px;
    color: #3d7eff;
    background: #e9f1ff;
    padding: 2px 10px;
    border-radius: 10px;
}

.chevron {
    flex-shrink: 0;
    font-size: 14px;
    color: #9aa4b8;
    transition: transform 0.25s ease;
}

.chevron.is-open {
    transform: rotate(90deg);
}

/* ---------- Nested tree guide ---------- */
.children {
    position: relative;
    padding-left: 22px;
    background: #fff;
}

.children::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 0;
    bottom: 16px;
    width: 2px;
    border-radius: 2px;
    background: linear-gradient(180deg, #e2ebfa, #f2f6fd);
}

.children>.album-node::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 28px;
    width: 12px;
    height: 2px;
    border-radius: 2px;
    background: #e2ebfa;
}

/* Remove trailing border on last child of each group */
.children>.album-node:last-child>.node-row,
.children>.album-node:last-child>.folder-node>.folder-head {
    border-bottom-color: transparent;
}
</style>
